import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { filter } from 'lodash';
import { Icon } from '@iconify/react';
import { sentenceCase } from 'change-case';
import plusFill from '@iconify/icons-eva/plus-fill';
import { LinkasRouterLink, useNavigate } from 'react-router-dom';
// material
import {
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination
} from '@mui/material';
// utils
import { toast } from 'react-hot-toast';
import { confirmAlert } from 'react-confirm-alert';
import moment from 'moment-timezone';
//
import * as appServices from '../services/aplikasi';
// components
import Page from '../components/Page';
import Label from '../components/Label';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import { AppListHead, AppListToolbar, AppMoreMenu } from '../components/_dashboard/aplikasi';
import USERLIST from '../_mocks_/user';
import isEligible from '../utils/roleEligibleGetter';
import AccessRequired from './AccessRequired';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'title', label: 'Name', alignRight: false },
  { id: 'company', label: 'Instansi', alignRight: false },
  { id: 'versions', label: 'Versi', alignRight: false },
  { id: 'createdAt', label: 'Created at', alignRight: false },
  { id: 'creator', label: 'Created by', alignRight: false },
  { id: '' }
];

//

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_user) =>
        _user?.title?.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
        _user?.company?.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

Aplikasi.propTypes = {
  user: PropTypes.object.isRequired
};

export default function Aplikasi({ user }) {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [apps, setApps] = React.useState([]);
  const [toggleRefresh, setToggleRefresh] = React.useState(false);
  const navigate = useNavigate();

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = apps.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const deleteData = async (rowData) => {
    confirmAlert({
      title: 'Konfirmasi',
      message: 'Apakah Anda yakin akan menghapus data ini?',
      buttons: [
        {
          label: 'Yakin',
          onClick: async () => {
            try {
              const body = { id: rowData.id };
              if (!body.id) return;
              toast
                .promise(appServices.deleteByID(body.id), {
                  loading: 'Menghapus data ...',
                  error: <b>Maaf, terjadi kesalahan</b>,
                  success: <b>Data berhasil dihapus</b>
                })
                .then(() => {
                  setToggleRefresh(!toggleRefresh);
                });
            } catch (error) {
              toast.error(error.message);
            }
          }
        },
        {
          label: 'Tidak',
          onClick: () => console.log('No')
        }
      ]
    });
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - apps.length) : 0;

  const filteredData = applySortFilter(apps, getComparator(order, orderBy), filterName);

  const isUserNotFound = filteredData.length === 0;

  const getApps = async () => {
    try {
      const body = {
        id: user?.attributes?.id_instansi,
        role: user?.attributes?.role,
        user_id: user?.attributes?.id
      };

      if (!body.id && !['admin', 'superadmin'].includes(body.role)) return;
      const result = await appServices.getAll(body);
      const lists = [];

      result.data.forEach((item) => {
        const { title, description, createdAt } = item?.attributes;
        const company = item?.relationships?.meta?.instansi?.name;
        const creator = item?.relationships?.meta?.user?.username;
        const versions = item?.meta?.versions?.length;
        const idRekomendasi = item?.meta?.id_rekomendasi;
        lists.push({
          id: item.id,
          title,
          description,
          createdAt,
          company,
          creator,
          versions,
          idRekomendasi
        });
      });

      setApps(lists);
    } catch (error) {
      toast.error(error.message);
    }
  };

  React.useEffect(() => {
    getApps();
  }, [toggleRefresh, user]);

  const createInitAplikasi = async () => {
    try {
      const body = {
        data: {
          attributes: {
            id_instansi: user?.attributes?.id_instansi,
            title: 'Nama Aplikasi',
            creator: user?.attributes?.id
          }
        }
      };
      const result = await appServices.createInit(body);

      if (result?.data?.id) {
        const id = result?.data?.id;
        toast.success('Formulir berhasil dibuat');
        setToggleRefresh(!toggleRefresh);
      } else {
        toast.error('Maaf, terjadi kesalahan');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Page title="User | Peladen Pemda DIY">
      {isEligible('page_app') ? (
        <Container>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
            <Typography variant="h4" gutterBottom>
              Pendataan Aplikasi
            </Typography>
            <Button
              variant="contained"
              onClick={() => createInitAplikasi()}
              startIcon={<Icon icon={plusFill} />}
            >
              New Form
            </Button>
          </Stack>

          <Card>
            <AppListToolbar
              numSelected={selected.length}
              filterName={filterName}
              onFilterName={handleFilterByName}
            />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <AppListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={apps.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                  />
                  <TableBody>
                    {filteredData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row) => {
                        const { id } = row;
                        const { title, versions, company, creator, createdAt, idRekomendasi } = row;

                        return (
                          <TableRow hover key={id} tabIndex={-1}>
                            <TableCell align="left" />
                            <TableCell align="left">{title}</TableCell>
                            <TableCell align="left">{company}</TableCell>
                            <TableCell align="left">{versions || 0}</TableCell>
                            <TableCell align="left">
                              {moment.tz(createdAt, 'Asia/Jakarta').format('YYYY-MM-DD')}
                            </TableCell>
                            <TableCell align="left">{creator}</TableCell>
                            <TableCell align="right">
                              <AppMoreMenu onDelete={deleteData} rowData={{ id, idRekomendasi }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                  {isUserNotFound && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                          <SearchNotFound searchQuery={filterName} />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>

            <TablePagination
              rowsPerPageOptions={[10, 20, 30]}
              component="div"
              count={apps.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Card>
        </Container>
      ) : (
        <AccessRequired />
      )}
    </Page>
  );
}
